/** Rotation FDR Finder — Fáze 3: plánování pro manažera (FPL Team ID / ruční sestava). */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CaretDown, CaretUp, Play, Warning, X, ArrowRight, UserCircle, CheckCircle, FloppyDisk, ClockCounterClockwise, Trash } from "@phosphor-icons/react";
import RotationDetail from "./RotationDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/rotation`;
const POS_ORDER = ["GKP", "DEF", "MID", "FWD"];
const TAG_STYLE = {
    "Lock rotation": "bg-fa-mint text-black",
    "Short-term punt": "bg-fa-yellow text-black",
    "Wildcard bridge": "bg-fa-sky text-black",
    "Bench cover": "border border-white/30 text-white/70",
    "Trap rotation": "bg-red-400 text-black",
    "Solid": "border border-white/20 text-white/60",
};
const num = "bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-16 focus:outline-none focus:border-fa-mint";

function AlertsPanel({ alerts }) {
    if (!alerts.length) return (
        <div className="border border-fa-mint/30 bg-fa-mint/5 p-3 font-mono text-[10px] text-fa-mint flex items-center gap-2" data-testid="sp-alerts-ok">
            <CheckCircle size={14} weight="fill" /> Žádné kolo, kde by se ti pozice rozbila — každá pozice má vždy aspoň jeden hratelný zápas (FDR ≤3).
        </div>
    );
    return (
        <div className="border border-red-400/40 bg-red-950/30 p-3 space-y-2" data-testid="sp-alerts">
            <p className="font-mono text-[10px] uppercase tracking-widest text-red-300 flex items-center gap-1.5">
                <Warning size={13} weight="fill" /> Kola, kde se ti rotace rozbíjí
            </p>
            {alerts.map((a) => (
                <div key={a.gw} className="font-mono text-[10px] text-white/70" data-testid={`sp-alert-gw-${a.gw}`}>
                    <span className="text-red-300 font-bold">GW{a.gw}</span>
                    {a.broken.map((b) => (
                        <span key={b.position} className="ml-2">
                            <span className="text-white/90 font-bold">{b.position}</span>
                            <span className="text-white/40"> (nejlepší FDR {b.best_fdr}): </span>
                            {b.fixtures.slice(0, 3).map((f) => `${f.name} ${f.opp} ${f.adj}`).join(" · ")}
                        </span>
                    ))}
                </div>
            ))}
        </div>
    );
}

function RotationsSection({ rotations, expanded, setExpanded }) {
    return POS_ORDER.map((pos) => {
        const rows = rotations[pos] || [];
        if (!rows.length) return null;
        return (
            <div key={pos} className="border border-white/10 bg-[#0d0d0d]" data-testid={`sp-rotations-${pos}`}>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 px-3 pt-2">Rotace v týmu — {pos}</p>
                <table className="w-full text-left">
                    <tbody>
                        {rows.slice(0, 5).map((r) => {
                            const key = `${pos}-${r.rank}`;
                            return (
                                <React.Fragment key={key}>
                                    <tr className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                                        onClick={() => setExpanded(expanded === key ? null : key)}>
                                        <td className="px-3 py-2 font-display font-bold text-sm">
                                            {r.players.map((p) => p.name).join(" + ")}
                                            <span className="font-mono text-[8px] uppercase text-white/40 ml-2">{r.teams.join(" · ")}</span>
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono text-[11px] font-bold text-fa-mint tabular-nums">{r.value_score}</td>
                                        <td className="px-2 py-2 text-right font-mono text-[10px] tabular-nums text-white/60">FDR {r.avg_rotation_fdr}</td>
                                        <td className="px-2 py-2 text-right font-mono text-[10px] text-fa-mint tabular-nums">{r.green_weeks}/{r.n_gws} green</td>
                                        <td className="px-2 py-2 text-right font-mono text-[10px] text-fa-yellow tabular-nums">{r.playable_weeks}/{r.n_gws} play</td>
                                        <td className="px-2 py-2 text-right font-mono text-[9px] text-red-300">
                                            {r.red_flag_gws.length ? r.red_flag_gws.map((g) => `GW${g}`).join(", ") : "—"}
                                        </td>
                                        <td className="px-2 py-2">
                                            <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 whitespace-nowrap ${TAG_STYLE[r.tag] || TAG_STYLE.Solid}`}>{r.tag}</span>
                                        </td>
                                        <td className="px-2 py-2 text-fa-sky">{expanded === key ? <CaretUp size={12} /> : <CaretDown size={12} />}</td>
                                    </tr>
                                    {expanded === key && (
                                        <tr className="border-b border-white/5 bg-black/40">
                                            <td colSpan={8}><RotationDetail row={r} showCs={pos === "GKP" || pos === "DEF"} /></td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    });
}

function TransfersSection({ transfers }) {
    const poss = POS_ORDER.filter((p) => transfers[p]?.length);
    if (!poss.length) return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3 font-mono text-[10px] text-white/50" data-testid="sp-transfers-none">
            Žádný přestup by výrazně nezlepšil tvoje rotace v tomto období — sestava sedí.
        </div>
    );
    return (
        <div className="space-y-2">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Doporučené přestupy (vylepší rotaci)</p>
            {poss.map((pos) => (
                <div key={pos} className="border border-white/10 bg-[#0d0d0d] p-3 space-y-2" data-testid={`sp-transfers-${pos}`}>
                    <p className="font-mono text-[9px] uppercase text-white/40">{pos}</p>
                    {transfers[pos].map((s, i) => (
                        <div key={i} className="font-mono text-[10px] flex items-center gap-2 flex-wrap" data-testid={`sp-transfer-${pos}-${i}`}>
                            <span className="text-red-300">OUT {s.out.name} £{s.out.price}</span>
                            <ArrowRight size={11} className="text-white/40" />
                            <span className="text-fa-mint font-bold">IN {s.in.name} ({s.in.team_short}) £{s.in.price}</span>
                            <span className="text-white/40">· drž {s.keep.name}</span>
                            <span className="text-fa-sky">· skóre +{s.delta}</span>
                            <span className="text-white/50">· FDR {s.old_avg_fdr} → {s.avg_fdr}</span>
                            {s.price_delta !== 0 && <span className="text-white/40">· {s.price_delta > 0 ? "+" : ""}£{s.price_delta}</span>}
                            {s.red_fixed.length > 0 && (
                                <span className="text-fa-yellow">· opraví {s.red_fixed.map((g) => `GW${g}`).join(", ")}</span>
                            )}
                            <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 ${TAG_STYLE[s.tag] || TAG_STYLE.Solid}`}>{s.tag}</span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function BridgesSection({ bridges }) {
    const poss = POS_ORDER.filter((p) => bridges[p]);
    if (!poss.length) return null;
    return (
        <div className="space-y-2">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Wildcard bridges — rotace z trhu, které pokryjí tvoje rozbitá kola</p>
            {poss.map((pos) => {
                const b = bridges[pos];
                return (
                    <div key={pos} className="border border-fa-sky/30 bg-[#0d0d0d] p-3 space-y-1.5" data-testid={`sp-bridges-${pos}`}>
                        <p className="font-mono text-[9px] uppercase text-fa-sky">
                            {pos} · rozbité: {b.bad_gws.map((g) => `GW${g}`).join(", ")}
                        </p>
                        {b.rows.length === 0 && (
                            <p className="font-mono text-[10px] text-white/50">Ani trh nenabízí rotaci, která by tato kola pokryla — zvaž lavičku nebo chip.</p>
                        )}
                        {b.rows.map((r, i) => (
                            <div key={i} className="font-mono text-[10px] flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-white/90">{r.players.map((p) => p.name).join(" + ")}</span>
                                <span className="text-white/40">{r.teams.join(" · ")}</span>
                                <span className="text-white/60">£{r.total_price}</span>
                                <span className="text-white/50">FDR {r.avg_rotation_fdr}</span>
                                <span className="text-fa-mint">{r.green_weeks}/{r.n_gws} green</span>
                                <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 ${TAG_STYLE[r.tag] || TAG_STYLE.Solid}`}>{r.tag}</span>
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function DiffPanel({ saved, diff }) {
    const date = new Date(saved.created_at).toLocaleDateString("cs");
    const rotChanges = diff.rotations.filter((r) => r.changed_players || Math.abs(r.delta) >= 0.05);
    return (
        <div className="border border-fa-yellow/40 bg-fa-yellow/5 p-3 space-y-1.5" data-testid="sp-diff">
            <p className="font-mono text-[9px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5">
                <ClockCounterClockwise size={13} /> Porovnání s plánem z {date}{saved.note ? ` · „${saved.note}"` : ""}
            </p>
            {!diff.changed && (
                <p className="font-mono text-[10px] text-fa-mint" data-testid="sp-diff-unchanged">Situace se od uložení nezměnila — plán pořád platí.</p>
            )}
            {diff.alerts_added.length > 0 && (
                <p className="font-mono text-[10px] text-red-300">Nově rozbitá kola: {diff.alerts_added.map((a) => `GW${a.gw} ${a.position}`).join(", ")}</p>
            )}
            {diff.alerts_resolved.length > 0 && (
                <p className="font-mono text-[10px] text-fa-mint">Vyřešená kola: {diff.alerts_resolved.map((a) => `GW${a.gw} ${a.position}`).join(", ")}</p>
            )}
            {rotChanges.map((r) => (
                <p key={r.position} className="font-mono text-[10px] text-white/70">
                    {r.position}: {r.changed_players
                        ? `nejlepší rotace se změnila (${r.old_players.join(" + ")} → ${r.new_players.join(" + ")})`
                        : `skóre rotace ${r.old_score} → ${r.new_score} (${r.delta > 0 ? "+" : ""}${r.delta})`}
                </p>
            ))}
            {diff.transfers_changed.map((t) => (
                <p key={t.position} className="font-mono text-[10px] text-fa-sky">
                    {t.position}: top přestup {t.old ? `${t.old.out} → ${t.old.in}` : "—"} ⇒ nyní {t.new ? `${t.new.out} → ${t.new.in}` : "žádný"}
                </p>
            ))}
        </div>
    );
}

export default function SquadPlanner() {
    const [teamId, setTeamId] = useState(localStorage.getItem("fpl_team_id") || "");
    const [gwFrom, setGwFrom] = useState(1);
    const [gwTo, setGwTo] = useState(6);
    const [fdrMode, setFdrMode] = useState("position");
    const [manual, setManual] = useState(false);
    const [allPlayers, setAllPlayers] = useState(null);
    const [search, setSearch] = useState("");
    const [picked, setPicked] = useState([]);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [note, setNote] = useState("");
    const [savedInfo, setSavedInfo] = useState(null);
    const [diff, setDiff] = useState(null);
    const [lastParams, setLastParams] = useState(null);

    useEffect(() => {
        const key = localStorage.getItem("rotation_plan_key");
        if (!key) return;
        axios.get(`${API}/plan/${key}`, { params: { compare: false } })
            .then((r) => setSavedInfo(r.data.saved))
            .catch(() => localStorage.removeItem("rotation_plan_key"));
    }, []);

    const loadPlayers = async () => {
        if (allPlayers) return;
        try {
            const r = await axios.get(`${API}/players`);
            setAllPlayers(r.data.players.filter((p) => p.price != null));
        } catch { toast.error("Nepodařilo se načíst seznam hráčů"); }
    };

    const run = async (params) => {
        setBusy(true);
        setExpanded(null);
        setDiff(null);
        setLastParams(params);
        try {
            const r = await axios.get(`${API}/squad-plan`, { params: {
                gw_from: gwFrom, gw_to: gwTo, fdr_mode: fdrMode, ...params,
            } });
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Načtení selhalo");
            if (e.response?.status === 409) { setManual(true); loadPlayers(); }
        } finally { setBusy(false); }
    };

    const loadTeam = () => {
        if (!teamId) { toast.error("Zadej FPL Team ID"); return; }
        localStorage.setItem("fpl_team_id", teamId);
        run({ team_id: teamId });
    };
    const runManual = () => {
        if (picked.length < 2) { toast.error("Vyber aspoň 2 hráče"); return; }
        run({ player_ids: picked.map((p) => p.id).join(",") });
    };

    const savePlan = async () => {
        if (!lastParams) return;
        const existing = localStorage.getItem("rotation_plan_key");
        try {
            const r = await axios.post(`${API}/plan`, {
                key: existing || null, ...lastParams, gw_from: gwFrom, gw_to: gwTo, fdr_mode: fdrMode, note,
            });
            localStorage.setItem("rotation_plan_key", r.data.key);
            setSavedInfo({ key: r.data.key, note, created_at: r.data.created_at, gw_from: gwFrom, gw_to: gwTo });
            setDiff(null);
            toast.success("Plán uložen — příště ti ukážu, co se změnilo");
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const comparePlan = async () => {
        const key = localStorage.getItem("rotation_plan_key");
        if (!key) return;
        setBusy(true);
        try {
            const r = await axios.get(`${API}/plan/${key}`);
            const s = r.data.saved;
            setGwFrom(s.gw_from); setGwTo(s.gw_to); setFdrMode(s.fdr_mode);
            setLastParams(s.team_id ? { team_id: s.team_id } : { player_ids: s.player_ids });
            setSavedInfo(s);
            setNote(s.note || "");
            setData(r.data.current);
            setDiff(r.data.diff);
            setExpanded(null);
        } catch (e) { toast.error(e.response?.data?.detail || "Porovnání selhalo"); }
        finally { setBusy(false); }
    };

    const deletePlan = async () => {
        const key = localStorage.getItem("rotation_plan_key");
        if (!key) return;
        try {
            await axios.delete(`${API}/plan/${key}`);
            localStorage.removeItem("rotation_plan_key");
            setSavedInfo(null);
            setDiff(null);
            toast.success("Plán smazán");
        } catch { toast.error("Smazání selhalo"); }
    };

    const results = useMemo(() => {
        if (!search.trim() || !allPlayers) return [];
        const q = search.trim().toLowerCase();
        return allPlayers.filter((p) => p.name.toLowerCase().includes(q) && !picked.some((x) => x.id === p.id)).slice(0, 10);
    }, [search, allPlayers, picked]);

    return (
        <div className="space-y-4" data-testid="squad-planner">
            {savedInfo && !diff && (
                <div className="border border-fa-sky/40 bg-fa-sky/5 p-3 flex items-center gap-3 flex-wrap font-mono text-[10px]" data-testid="sp-saved-banner">
                    <ClockCounterClockwise size={14} className="text-fa-sky" />
                    <span className="text-white/80">
                        Uložený plán z {new Date(savedInfo.created_at).toLocaleDateString("cs")} (GW{savedInfo.gw_from}–{savedInfo.gw_to}){savedInfo.note ? ` · „${savedInfo.note}"` : ""}
                    </span>
                    <button type="button" onClick={comparePlan} disabled={busy} data-testid="sp-compare"
                            className="ml-auto uppercase tracking-widest px-3 py-1.5 bg-fa-sky text-black font-bold hover:opacity-90 disabled:opacity-40">
                        {busy ? "Počítám…" : "Načíst a porovnat"}
                    </button>
                    <button type="button" onClick={deletePlan} data-testid="sp-delete-plan" title="Smazat uložený plán"
                            className="text-red-300 hover:text-red-200"><Trash size={13} /></button>
                </div>
            )}
            <div className="border border-white/10 bg-[#0d0d0d] p-3 flex items-end gap-3 flex-wrap">
                <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                    <div>FPL Team ID</div>
                    <input type="number" value={teamId} onChange={(e) => setTeamId(e.target.value)} placeholder="např. 158204"
                           className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-28 focus:outline-none focus:border-fa-mint placeholder:text-white/25"
                           data-testid="sp-team-id" />
                </label>
                <button type="button" onClick={loadTeam} disabled={busy} data-testid="sp-load"
                        className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 bg-fa-mint text-black font-bold hover:opacity-90 disabled:opacity-40">
                    <UserCircle size={11} weight="fill" className="inline mb-0.5 mr-1" /> {busy ? "Načítám…" : "Načíst tým"}
                </button>
                <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                    <div>GW od–do</div>
                    <div className="flex gap-1">
                        <input type="number" min={1} max={38} value={gwFrom} onChange={(e) => setGwFrom(+e.target.value)} className={num} data-testid="sp-gw-from" />
                        <input type="number" min={1} max={38} value={gwTo} onChange={(e) => setGwTo(+e.target.value)} className={num} data-testid="sp-gw-to" />
                    </div>
                </label>
                <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                    <div>FDR režim</div>
                    <div className="flex gap-1">
                        {[["position", "Dle pozice"], ["official", "Oficiální"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setFdrMode(k)}
                                    className={`px-2 py-1.5 font-mono text-[10px] border ${fdrMode === k ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer font-mono text-[10px] text-white/70 pb-2" data-testid="sp-manual-toggle">
                    <input type="checkbox" checked={manual} onChange={(e) => { setManual(e.target.checked); if (e.target.checked) loadPlayers(); }} className="accent-[#9EE8B4]" />
                    Ruční sestava (mimo sezónu)
                </label>
            </div>

            {manual && (
                <div className="border border-white/10 bg-[#0d0d0d] p-3 space-y-2" data-testid="sp-manual">
                    <div className="flex items-center gap-2 flex-wrap">
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hledej hráče — Salah, Haaland…"
                               className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-64 focus:outline-none focus:border-fa-mint placeholder:text-white/25"
                               data-testid="sp-search" />
                        <span className="font-mono text-[9px] text-white/40">{picked.length}/15 hráčů</span>
                        <button type="button" onClick={runManual} disabled={busy || picked.length < 2} data-testid="sp-run-manual"
                                className="ml-auto font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-yellow text-black font-bold hover:bg-fa-yellowHover disabled:opacity-40">
                            <Play size={11} weight="fill" className="inline mb-0.5 mr-1" /> Analyzovat sestavu
                        </button>
                    </div>
                    {results.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap">
                            {results.map((p) => (
                                <button key={p.id} type="button" data-testid={`sp-add-${p.id}`}
                                        onClick={() => { if (picked.length >= 15) { toast.error("Max 15 hráčů"); return; } setPicked([...picked, p]); setSearch(""); }}
                                        className="font-mono text-[10px] px-2 py-1 border border-white/15 text-white/70 hover:border-fa-mint hover:text-white">
                                    + {p.name} <span className="text-white/40">{p.team_short} {p.position} £{p.price}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {picked.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap">
                            {POS_ORDER.flatMap((pos) => picked.filter((p) => p.position === pos)).map((p) => (
                                <span key={p.id} className="font-mono text-[10px] px-2 py-1 bg-white/5 border border-white/10 flex items-center gap-1.5">
                                    <span className="text-white/40">{p.position}</span> {p.name}
                                    <button type="button" onClick={() => setPicked(picked.filter((x) => x.id !== p.id))} className="text-red-300 hover:text-red-200" data-testid={`sp-remove-${p.id}`}>
                                        <X size={10} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {data && (
                <>
                    {diff && savedInfo && <DiffPanel saved={savedInfo} diff={diff} />}
                    {data.manager && (
                        <div className="border border-white/10 bg-[#0d0d0d] p-3 font-mono text-[10px] text-white/70 flex items-center gap-3 flex-wrap" data-testid="sp-manager">
                            <UserCircle size={16} weight="fill" className="text-fa-mint" />
                            <span className="font-bold text-white">{data.manager.team_name}</span>
                            <span>{data.manager.name}</span>
                            {data.manager.overall_rank && <span className="text-white/40">OR {data.manager.overall_rank.toLocaleString("cs")}</span>}
                            <span className="text-white/40">picks z GW{data.manager.event}</span>
                            <span className="ml-auto text-white/40">GW{data.gw_from}–{data.gw_to}{data.xg_teams > 0 && <span className="text-fa-mint/70"> · xG: SportMonks ({data.xg_teams})</span>}</span>
                        </div>
                    )}
                    <div className="font-mono text-[10px] text-white/50 flex gap-1.5 flex-wrap" data-testid="sp-squad">
                        {POS_ORDER.flatMap((pos) => (data.squad || []).filter((p) => p.position === pos)).map((p) => (
                            <span key={p.id} className="px-2 py-1 bg-white/5 border border-white/10">
                                <span className="text-white/35">{p.position}</span> <span className="text-white/85">{p.name}</span> <span className="text-white/35">{p.team_short}</span>
                            </span>
                        ))}
                    </div>
                    {data.missing_ids?.length > 0 && (
                        <p className="font-mono text-[9px] text-fa-yellow">{data.missing_ids.length} hráčů z picks se nepodařilo napárovat na aktuální data.</p>
                    )}
                    <AlertsPanel alerts={data.alerts || []} />
                    <RotationsSection rotations={data.rotations || {}} expanded={expanded} setExpanded={setExpanded} />
                    <TransfersSection transfers={data.transfers || {}} />
                    <BridgesSection bridges={data.bridges || {}} />
                    <div className="flex items-center gap-2 flex-wrap border border-white/10 bg-[#0d0d0d] p-3">
                        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Poznámka k plánu (volitelné)…"
                               className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px] w-72 focus:outline-none focus:border-fa-mint placeholder:text-white/25"
                               data-testid="sp-note" />
                        <button type="button" onClick={savePlan} disabled={busy || !lastParams} data-testid="sp-save-plan"
                                className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-40">
                            <FloppyDisk size={11} className="inline mb-0.5 mr-1" /> Uložit plán
                        </button>
                        <span className="font-mono text-[9px] text-white/35">Při dalším otevření ti ukážu, co se od uložení změnilo (FDR, rozbitá kola, přestupy).</span>
                    </div>
                </>
            )}
        </div>
    );
}
